/* Elwatanya Plast — redesign behaviour.
   Two jobs: kill phantom sale badges on product cards, and drive the mobile
   sticky purchase bar. Cart-driven UI (free-shipping progress, variant cards,
   line total) lives in elw-cart-ui.js. */
(function () {
  'use strict';

  /* Write only when the value actually changes. The sticky bar lives INSIDE the
     product-info container that the observer below watches, so an unconditional
     write would retrigger the observer and loop forever. */
  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  /* Read what a SIGHTED customer reads.

     textContent ignores CSS, so it returns screen-reader-only text too. Dawn
     appends `<span class="visually-hidden label-unavailable">` — the locale
     string products.product.variant_sold_out_or_unavailable — inside EVERY
     variant <label>, available or not. Reading the label raw therefore printed
     "نفدت الكمية أو هذا المتغير غير متاح" on the sticky bar of a product that is
     in stock. Dawn's price block hides its "Regular price" / "Sale price"
     labels the same way.

     Clone, drop the hidden nodes, then read. The span itself stays in the DOM —
     screen readers still need it — and the locale string is untouched. */
  function visibleText(el) {
    if (!el) return '';
    var copy = el.cloneNode(true);
    copy.querySelectorAll('.visually-hidden, .visually-hidden--inline, .label-unavailable')
      .forEach(function (hidden) { hidden.parentNode.removeChild(hidden); });
    return copy.textContent.replace(/\s+/g, ' ').trim();
  }

  /* ----------------------------------------------------------------------
     Phantom sale badges.
     price.liquid marks a price block with .price--elw-no-badge when its
     compare-at price belongs to a different variant (or is implausible).
     card-product.liquid renders its own badge outside that block, so mirror
     the decision here. A sold-out badge must never be hidden.
     ---------------------------------------------------------------------- */
  function hidePhantomBadges(root) {
    var scope = root || document;
    scope.querySelectorAll('.price--elw-no-badge').forEach(function (priceEl) {
      if (priceEl.classList.contains('price--sold-out')) return;
      var card = priceEl.closest('.card-wrapper') || priceEl.closest('.card');
      if (!card) return;
      card.querySelectorAll('.card__badge .badge').forEach(function (badge) {
        if (!badge.classList.contains('elw-hidden-badge')) {
          badge.classList.add('elw-hidden-badge');
        }
      });
    });
  }

  /* ----------------------------------------------------------------------
     Sticky mobile purchase bar.
     Appears once the real add-to-cart button leaves the viewport. Its button
     submits the actual product form via the form attribute, so variant,
     quantity, cart and checkout behaviour are untouched.
     ---------------------------------------------------------------------- */

  /* How long to wait before believing a disabled button. Dawn debounces the
     variant change by 300ms and then fetches the re-rendered section, so the
     round trip is comfortably under this on a normal connection. */
  var VARIANT_SETTLE = 700;

  function initStickyBar() {
    var bar = document.querySelector('[data-elw-stickybar]');
    if (!bar || bar.dataset.elwInit === '1') return;
    bar.dataset.elwInit = '1';

    var sectionId = bar.getAttribute('data-section-id');
    var info = document.getElementById('ProductInfo-' + sectionId);
    var anchor = document.getElementById('ProductSubmitButton-' + sectionId);
    if (!info) info = bar.closest('.product__info-container');
    if (!anchor && info) anchor = info.querySelector('.product-form__submit');
    if (!info || !anchor) return;

    var variantEl = bar.querySelector('[data-elw-stickybar-variant]');
    var priceEl = bar.querySelector('[data-elw-stickybar-price]');
    var btn = bar.querySelector('[data-elw-stickybar-btn]');
    var btnText = bar.querySelector('[data-elw-stickybar-btn-text]');
    var soldOutTimer;

    function currentVariantLabel() {
      var checked = info.querySelector('.product-form__input input[type="radio"]:checked');
      if (checked) {
        var label = info.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(checked.id) : checked.id) + '"]');
        if (label) return visibleText(label);
        return checked.value;
      }
      var select = info.querySelector('.product-form__input select');
      if (select && select.selectedIndex >= 0) return select.options[select.selectedIndex].text.trim();
      var selected = info.querySelector('[data-selected-value]');
      return visibleText(selected);
    }

    function currentPrice() {
      var priceBlock = info.querySelector('.price');
      if (!priceBlock) return '';
      var node =
        priceBlock.querySelector('.price__sale .price-item--sale') ||
        priceBlock.querySelector('.price__regular .price-item--regular') ||
        priceBlock.querySelector('.price-item');
      return visibleText(node);
    }

    /* Both directions. The old code only ever ran `if (disabled)`, so the label
       could be written to "sold out" but never written back. */
    function paintLabel(soldOut) {
      if (!btnText) return;
      var fallback = btnText.textContent;
      setText(btnText, btn.getAttribute(soldOut ? 'data-sold-out-text' : 'data-add-text') || fallback);
    }

    function sync() {
      setText(variantEl, currentVariantLabel());
      setText(priceEl, currentPrice());
      if (!btn) return;

      /* A DISABLED BUTTON IS NOT PROOF OF SOLD OUT.

         product-info.js calls resetProductFormState() → toggleSubmitButton(true)
         at the START of every variant change, and only re-enables once the
         re-rendered section lands. This observer therefore sees `disabled` for a
         few hundred milliseconds on a product that is fully in stock.

         Combined with the one-way write above, that transient used to LATCH:
         the first variant switch stamped "نفد المخزون" onto the bar and nothing
         ever cleared it, so it followed whichever variant had last been switched
         away from instead of real stock.

         The button's own disabled state still mirrors instantly — that one is
         safety-critical and erring toward "cannot submit" is the safe direction.
         Only the LABEL waits for the state to outlive Dawn's round trip. */
      var disabled = anchor.hasAttribute('disabled');
      if (btn.disabled !== disabled) btn.disabled = disabled;

      clearTimeout(soldOutTimer);
      if (!disabled) {
        paintLabel(false);
      } else {
        soldOutTimer = setTimeout(function () {
          if (anchor.hasAttribute('disabled')) paintLabel(true);
        }, VARIANT_SETTLE);
      }
    }

    function show(visible) {
      bar.classList.toggle('is-visible', visible);
      document.body.classList.toggle('elw-stickybar-active', visible);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            show(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { rootMargin: '0px 0px -40% 0px' }
      ).observe(anchor);
    }

    new MutationObserver(sync).observe(info, { subtree: true, childList: true, attributes: true });
    info.addEventListener('change', sync);
    sync();
  }

  function init() {
    hidePhantomBadges();
    initStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Sections re-render on cart updates, quick-add and variant changes. */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

  /* Capped, not a plain debounce. Anything mutating faster than the window —
     the rotating announcement bar does, every 50ms — keeps resetting the timer,
     and the sweep then never runs at all: phantom badges would stay visible on
     every re-rendered price block. */
  var BADGE_DEBOUNCE = 120, BADGE_MAX_WAIT = 500;
  var badgeSweep, badgeQueued = 0;

  new MutationObserver(function () {
    var now = Date.now();
    if (!badgeQueued) badgeQueued = now;
    if (now - badgeQueued >= BADGE_MAX_WAIT) {
      clearTimeout(badgeSweep); badgeQueued = 0; hidePhantomBadges(); return;
    }
    clearTimeout(badgeSweep);
    badgeSweep = setTimeout(function () { badgeQueued = 0; hidePhantomBadges(); }, BADGE_DEBOUNCE);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
